import { useCallback, useState } from "react";
import { toast } from "sonner";
import { apiFetch } from "@/lib/api";
import { uploadBlob } from "@/lib/uploadBlob";
import useKagamiBridge from "@/hooks/useKagamiBridge";
import { closestAspectKey } from "./PropertiesPanel";
import { hashNodes } from "./sceneSnapshot";
import { GEN_AI_MODELS } from "@kagami/shared/sceneRenderModels";

// The aspect isn't stored in sceneSettings — it's read off whichever camera
// is currently piloted (see PropertiesPanel.jsx, where its options are
// already scoped to this same model) — so the preset has to be resolved
// fresh here rather than cached alongside genAiModelId/aiResolutionKey.
// Falls back to the first resolution for the current aspect if the stored
// key doesn't exist there (e.g. the model or camera aspect changed since).
export function resolveAiRenderPreset({
  genAiModelId,
  aiResolutionKey,
  cameraAspectRatio,
}) {
  const model =
    GEN_AI_MODELS.find((m) => m.model_id === genAiModelId) ?? GEN_AI_MODELS[0];
  const aspectOptions = [...new Set(model.camera_presets.map((p) => p.aspect))];
  const aspectKey = closestAspectKey(
    cameraAspectRatio ?? 16 / 9,
    aspectOptions,
  );
  const resolutionOptions = model.camera_presets
    .filter((p) => p.aspect === aspectKey)
    .map((p) => p.resolution);
  const resolutionKey = resolutionOptions.includes(aiResolutionKey)
    ? aiResolutionKey
    : resolutionOptions[0];
  const preset = model.camera_presets.find(
    (p) => p.aspect === aspectKey && p.resolution === resolutionKey,
  );
  return { model, aspectKey, resolutionOptions, resolutionKey, preset };
}

// "Save as Asset" is allowed whenever the asset hasn't already been promoted
// into the normal library — i.e. it's still scoped "scene3DRender", or it
// was promoted before but has since been archived from the assets view (in
// which case saving un-archives it — see saveAsAsset below). Once it's a
// plain, non-archived "default"-scope asset, there's nothing left to do.
export function canSaveAiRenderAsAsset(asset) {
  return (
    !!asset && (!!asset.archived || !!asset.scope?.includes("scene3DRender"))
  );
}

// A `{{"type":"asset",...}}` mention with no `assetId` — the notation the
// rest of the app uses for a catalogued Asset, but resolved inline off its
// own `url`/`name` instead of a DB fetch (see ExpandPromptService.ts's
// `__buildAssetEntry`), since none of Scene3D's reference images (or its own
// beauty render) are ever promoted into a real Asset doc. `smart: true`
// dispatch (see generate() below) hands this off to that same resolution the
// rest of the app already uses, which also assigns each one its "@imageN"
// label — no manual counter or dedup-by-url needed here anymore.
function assetMention(url, name) {
  return `{${JSON.stringify({ type: "asset", url, name, mediaType: "image" })}}`;
}

function formatBoundingBox({ x, y, width, height }) {
  return `x: ${x.toFixed(3)}, y: ${y.toFixed(3)}, width: ${width.toFixed(3)}, height: ${height.toFixed(3)}`;
}

// Builds the smart prompt directly from the scene data rather than embedding
// the scene.json and telling the model to go read it — models follow prose
// instructions describing what's in front of them far more reliably than a
// pointer into a structured blob sitting below. `sceneJson` is buildSceneJson's
// raw output (plain reference-image URLs); every one of those becomes an
// inline asset mention rather than a pre-resolved label.
function buildPrompt(sceneJson, beautyUrl, textureReferenceUrl) {
  const sections = [
    `You are given a reference image describing one exact camera shot of a virtual 3D scene.
${assetMention(beautyUrl, "Beauty Render")}: the base composition, lighting, and materials to start from. Preserve the exact camera framing, object silhouettes/positions, and occlusion it shows — only enhance the surface look; do not add, remove, or reposition anything.
Camera fov: ${sceneJson.camera.fov}`,
  ];

  if (sceneJson.textureReference && textureReferenceUrl) {
    sections.push(
      `${assetMention(textureReferenceUrl, "Texture Reference")}: Texture Reference — ${sceneJson.textureReference.note}`,
    );
  }

  const worldLines = [];
  if (sceneJson.world.description) {
    worldLines.push(`Scene description: ${sceneJson.world.description}`);
  }
  if (sceneJson.world.referenceImages.length) {
    worldLines.push(
      `Scene reference images (overall mood/setting for the whole shot): ${sceneJson.world.referenceImages
        .map((url) => assetMention(url, "Scene reference"))
        .join(", ")}`,
    );
  }
  if (worldLines.length) sections.push(worldLines.join("\n"));

  // Overall lighting mood for the whole shot (buildEnvironmentInfo in
  // renderPipeline.js) — deliberately unnumbered bullets for the per-light
  // entries, unlike "Objects"/"Lights visible in the shot" below: those use
  // "Object N"/"Light N" as anchors other facts (reference-image mentions)
  // point back to, but nothing else in the prompt ever refers back to one of
  // these, and reusing "Light N" here too would misleadingly suggest it's
  // the same numbering as the *different*, filtered "Lights visible in the
  // shot" list further down.
  if (sceneJson.environment) {
    const env = sceneJson.environment;
    const environmentLines = [
      `Ambient fill light — color: ${env.ambientColor}, intensity: ${env.ambientIntensity}`,
    ];
    if (env.lighting.length) {
      environmentLines.push(
        ...env.lighting.map(
          (light) =>
            `- color: ${light.color}, intensity: ${light.intensity}, position relative to camera: ${light.position.join(" ")}`,
        ),
      );
    }
    sections.push(
      `Scene lighting setup (use this to inform the overall mood, color temperature, and light direction of the render):\n${environmentLines.join("\n")}`,
    );
  }

  if (sceneJson.objects.length) {
    const objectLines = sceneJson.objects.map((obj, i) => {
      const facts = [];
      if (obj.boundingBox) {
        facts.push(
          `coordinates (normalized screen space, 0,0 top-left to 1,1 bottom-right): ${formatBoundingBox(obj.boundingBox)}`,
        );
      }
      if (obj.partiallyVisible) {
        facts.push(
          `partially out of frame — cut off on the ${obj.partiallyVisibleSide.join(" ")} side of the shot; the object continues past this edge, its true extent is larger than the bounding box shown`,
        );
      }
      if (obj.description) facts.push(`description: ${obj.description}`);
      if (obj.referenceImages?.length) {
        facts.push(
          `references: ${obj.referenceImages
            .map((url) => assetMention(url, `Object ${i + 1} reference`))
            .join(", ")}`,
        );
      }
      return `- Object ${i + 1} — ${facts.join("; ")}`;
    });
    sections.push(`Objects visible in the shot:\n${objectLines.join("\n")}`);
  }

  if (sceneJson.lights.length) {
    const lightLines = sceneJson.lights.map((light, i) => {
      const facts = [];
      if (light.boundingBox) {
        facts.push(
          `coordinates (normalized screen space, 0,0 top-left to 1,1 bottom-right): ${formatBoundingBox(light.boundingBox)}`,
        );
      }
      if (light.description) facts.push(`description: ${light.description}`);
      if (light.referenceImages?.length) {
        facts.push(
          `references: ${light.referenceImages
            .map((url) => assetMention(url, `Light ${i + 1} reference`))
            .join(", ")}`,
        );
      }
      return `- Light ${i + 1} — ${facts.join("; ")}`;
    });
    sections.push(`Lights visible in the shot:\n${lightLines.join("\n")}`);
  }

  return sections.join("\n\n");
}

// Drives the AI render overlay shown on top of the 3D viewport: renders the
// beauty pass off the live canvas, uploads it, fires the (paid) generation
// call, and polls it through to a finished image — one instant action, no
// local preview-and-confirm gate. `overrides` lets a caller pass a
// just-applied genAiModelId/aiResolutionKey that hasn't round-tripped through
// React state yet (e.g. the settings modal's "Render" button).
//
// The finished renders themselves aren't held here — `onGenerated` hands the
// full Asset doc for each one to the caller (Scene3DEditor.jsx), which owns
// the persisted history array (just a list of ids — see Scene3D.js) since
// that's scene state saved alongside nodes/sceneSettings, not something
// transient to this one operation. Handing back the whole asset (rather than
// a purpose-built record) means url/key/scope/archived/etc. are always
// exactly what's in the DB, never a separate copy that can drift.
export default function useAiRender({
  viewportRef,
  show,
  nodes,
  sceneSettings,
  folderId,
  onGenerated,
}) {
  const { runTaskAndPoll } = useKagamiBridge();
  const [phase, setPhase] = useState("idle"); // idle | rendering | generating | error
  const [errorMessage, setErrorMessage] = useState(null);
  const [savingAsset, setSavingAsset] = useState(false);

  const generate = useCallback(
    async (overrides = {}) => {
      if (!show?._id) return;
      const activeCameraId = viewportRef.current?.getActiveCameraId();
      // No piloted scene camera — the free-fly camera has no real aspect/fov
      // of its own to render from, so there's nothing correct to generate
      // against. The Toolbar's Render button already disables itself for
      // this same case; this guard is what makes that a hard rule rather
      // than just a UI nicety.
      if (!activeCameraId) return;
      const activeCameraNode = nodes.find((n) => n._id === activeCameraId);
      const { model, preset } = resolveAiRenderPreset({
        genAiModelId: overrides.genAiModelId ?? sceneSettings.genAiModelId,
        aiResolutionKey:
          overrides.aiResolutionKey ?? sceneSettings.aiResolutionKey,
        cameraAspectRatio: activeCameraNode?.aspect,
      });
      if (!preset) return;

      setPhase("rendering");
      setErrorMessage(null);
      try {
        const rendered = await viewportRef.current?.renderAIPipeline({
          width: preset.width,
          height: preset.height,
        });
        if (!rendered) throw new Error("Viewport isn't ready yet");

        setPhase("generating");
        const beauty = await uploadBlob(rendered.beautyBlob, {
          folder: "scenes3d/ai-render",
          fileName: `beauty-${Date.now()}.png`,
        });
        // Only present when a projection was active for this render (see
        // renderPipeline.js's runAIRenderPipeline) — the beauty render above
        // always shows the scene's own real materials, never this.
        const textureReference = rendered.textureReferenceBlob
          ? await uploadBlob(rendered.textureReferenceBlob, {
              folder: "scenes3d/ai-render",
              fileName: `texture-reference-${Date.now()}.png`,
            })
          : null;
        // generate_assets + assetScope routes the worker's completion
        // handler (falHandler.ts) to Asset.create instead of
        // Generation.create, scoped "scene3DRender" so it stays out of the
        // main assets grid until promoted (see saveAsAsset below). `smart:
        // true` routes the prompt's asset mentions (see buildPrompt/
        // assetMention above) through the same server-side expansion
        // (ExpandPromptService, executeTask.ts) every 2D generation already
        // uses, which resolves each mention's image, assigns it its
        // "@imageN" label, and fills image_urls/prompt for the model.
        const result = await runTaskAndPoll({
          modelId: model.model_id,
          smart: true,
          smartPrompt: buildPrompt(
            rendered.sceneJson,
            beauty.url,
            textureReference?.url,
          ),
          payload: { ...preset.value },
          showId: show._id,
          group: crypto.randomUUID(),
          generate_assets: true,
          assetScope: "scene3DRender",
        });

        const assetRes = await apiFetch(
          `/api/shows/${show._id}/assets?taskId=${result._id}`,
        );
        const asset = await assetRes.json();
        if (!asset?.url) throw new Error("Render finished without an image");

        // Frozen scene snapshot + camera pose at the moment this render was
        // generated — the basis for AiRenderOverlay's "Create Projection"
        // eligibility check (Scene3DEditor.jsx compares `nodesHash` against
        // the *current* live nodes) and for the projection itself, once
        // created (its pose never changes again — see Scene3D.js's
        // projectionSchema).
        const renderContext = {
          cameraNodeId: activeCameraId,
          cameraName: activeCameraNode?.name,
          cameraTransform: {
            position: activeCameraNode?.transform?.position,
            rotation: activeCameraNode?.transform?.rotation,
          },
          fov: activeCameraNode?.fov,
          aspect: activeCameraNode?.aspect,
          nodesHash: hashNodes(nodes),
        };

        onGenerated?.(asset, renderContext);
        setPhase("idle");
      } catch (err) {
        console.error("AI render generation failed:", err);
        setErrorMessage(err.message || "Generation failed");
        setPhase("error");
      }
    },
    [show, nodes, sceneSettings, viewportRef, runTaskAndPoll, onGenerated],
  );

  const dismiss = useCallback(() => {
    setPhase("idle");
    setErrorMessage(null);
  }, []);

  // Every render already exists as an Asset (scope "scene3DRender", created
  // alongside the render itself — see generate() above) so "saving" one just
  // flips it into the main library's default scope (and un-archives it —
  // see canSaveAiRenderAsAsset below) rather than creating a second copy. It
  // keeps whatever generic name falHandler.ts gave it; the user renames it
  // from the assets view like any other asset. `folderId` is whichever
  // folder the assets grid was open on when the 3D editor was launched (see
  // Scene3DEditor.jsx/AssetsEditor's `currentFolderId`) — the render never
  // had a real folder before this (falHandler.ts leaves it at root), so this
  // is its first placement, unlike a move that would need to respect
  // wherever the user had since filed it.
  //
  // Returns the updated asset on success (null otherwise) — the caller
  // (Scene3DEditor.jsx) splices it back into its local history array so the
  // overlay's derived "already saved" check reflects the DB immediately,
  // without a second fetch.
  const saveAsAsset = useCallback(
    async (asset) => {
      if (!asset?._id || !canSaveAiRenderAsAsset(asset) || !show?._id)
        return null;
      setSavingAsset(true);
      try {
        const res = await apiFetch(
          `/api/shows/${show._id}/assets/${asset._id}`,
          {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              scope: ["default"],
              folder: folderId || null,
              archived: false,
            }),
          },
        );
        const updated = await res.json();
        toast.success("Asset Saved");
        return updated;
      } catch (err) {
        console.error("Save as asset failed:", err);
        toast.error("Failed to save as asset");
        return null;
      } finally {
        setSavingAsset(false);
      }
    },
    [show, folderId],
  );

  return { phase, errorMessage, generate, dismiss, saveAsAsset, savingAsset };
}
